import React, { useMemo, useState, useEffect, useCallback } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/contactUs/registeringComplaintStyle';
import { makeStyles } from '@material-ui/core/styles';
import { translate } from 'components/Translation';
import CustomInput from 'components/CustomInput/CustomInput';
import { BodyS } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { finalize } from 'rxjs/operators';
import classNames from 'classnames';
import TextField from '@material-ui/core/TextField';
import CustomButton from 'components/CustomButtons/Button';
import { SearchService } from 'services/search/search';
import { SingleSearchNumber } from 'utils/api/models/search/singleSearchNumber';
import Dropdown from 'components/CustomDropdownWithSearch';
import NumberInfo from './NumberInfo';
import { SingleTroubleTicketingCategory } from 'utils/api/models/troubleTicketing/singleCategory';
import { SingleTroubleTicketingSelectOption } from 'utils/api/models/troubleTicketing/singleSelectOption';
import CustomSelect from 'components/CustomSelect/CustomSelect';
import { CreateOsmInteractionData } from 'utils/api/payloads/microApi';
import { userNameSelector } from 'redux/modules/session/selectors';
import { useSelector } from 'react-redux';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import UploadFiles from 'components/UploadFiles/UploadFiles';

// @ts-ignore
const useStyles = makeStyles(styles);

const MIN_SEARCH_TEXT_LENGTH = 3;
const MAX_NUMBER_OF_SUGGESTIONS = 5;
const MAX_NUMBER_LENGTH = 8;

interface ISingleTabProps {
	category: SingleTroubleTicketingCategory;
	sendData: (data: CreateOsmInteractionData) => void;
	corpAccounts: SingleCorpAccount[];
}

const SingleTab = ({ category, sendData, corpAccounts }: ISingleTabProps) => {
	const classes = useStyles();
	const [number, setNumber] = useState<string>('');
	const [billingAccountNumber, setBillingAccountNumber] = useState<string>('');
	const [contactNumber, setContactNumber] = useState<string>('');
	const [numbers, setNumbers] = useState<SingleSearchNumber[]>([]);
	const [alternativeNumber, setAlternativeNumber] = useState<string>('');
	const [emailAddress, setEmailAddress] = useState<string>('');
	const [numberInfo, setNumberInfo] = useState<SingleSearchNumber | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [message, setMessage] = useState<string>('');
	const [selectedSubject, setSelectedSubject] = useState<string>('');
	const [attachments, setAttachments] = useState<FormData>(new FormData());
	const username = useSelector(userNameSelector);
	const isOthersTab = category.id === 'OTHERS';
	const isBillingsTab = category.id === 'BILLINGS';

	const slicedNumbers = useMemo(() => {
		return numbers.slice(0, MAX_NUMBER_OF_SUGGESTIONS - 1);
	}, [numbers]);

	const suggestions: types.troubleTicketing.ISingleSuggestion[] = useMemo(() => {
		return slicedNumbers.map((singleNumber) => ({
			name: singleNumber.accountNum,
			value: singleNumber.accountType,
		}));
	}, [slicedNumbers]);

	const getNumbers = useCallback(() => {
		setLoading(true);
		SearchService.searchNumbersAndUsersRestPost(number)
			.pipe(finalize(() => setLoading(false)))
			.subscribe(({ numbers }) => {
				setNumbers(numbers);
			});
	}, [number]);

	const onSuggestionClick = (suggestion: types.troubleTicketing.ISingleSuggestion) => {
		const _numberInfo = slicedNumbers.find(
			({ accountNum, accountType }) => accountNum === suggestion.name && accountType === suggestion.value
		);
		if (_numberInfo) {
			setNumberInfo(_numberInfo);
		}
	};

	useEffect(() => {
		if (number.length >= MIN_SEARCH_TEXT_LENGTH) {
			getNumbers();
		} else {
			setNumbers([]);
		}
	}, [number, getNumbers]);

	const singleCorpAccount: SingleCorpAccount | undefined = useMemo(() => {
		return corpAccounts.find(({ accountId }) => accountId === numberInfo?.accountId);
	}, [corpAccounts, numberInfo]);

	const selectedOption: SingleTroubleTicketingSelectOption | undefined = useMemo(() => {
		switch (category.id) {
			case 'TECHNICAL':
				if (numberInfo) {
					return category.select.elements.find(
						({ displayName }) => displayName && displayName.indexOf(numberInfo.accountType) >= 0
					);
				}
				break;
			case 'BILLINGS':
				if (numberInfo) {
					const singleOptionElement = category.select.elements.find(
						(singleElement) => singleElement.displayName === selectedSubject
					);
					if (singleOptionElement && singleOptionElement.select) {
						return singleOptionElement.select.elements.find(
							({ displayName }) => displayName && displayName.indexOf(numberInfo.accountType) >= 0
						);
					}
					return singleOptionElement;
				}
				break;
			case 'OTHERS':
				return category.select.elements.find((singleElement) => singleElement.displayName === selectedSubject);
		}
		return undefined;
	}, [category, numberInfo, selectedSubject]);

	const buttonDisabled = useMemo(() => {
		if (isOthersTab) {
			return (
				billingAccountNumber.length === 0 ||
				contactNumber.length !== MAX_NUMBER_LENGTH ||
				alternativeNumber.length !== MAX_NUMBER_LENGTH ||
				!selectedOption ||
				message.length === 0 ||
				contactNumber === alternativeNumber ||
				emailAddress.length === 0 ||
				emailNotValid(emailAddress)
			);
		}
		return (
			!selectedOption ||
			!message ||
			alternativeNumber.length !== MAX_NUMBER_LENGTH ||
			number.length !== MAX_NUMBER_LENGTH ||
			number === alternativeNumber ||
			emailAddress.length === 0 ||
			emailNotValid(emailAddress)
		);
	}, [
		isOthersTab,
		billingAccountNumber,
		contactNumber,
		alternativeNumber,
		selectedOption,
		message,
		number,
		emailAddress,
	]);

	const onSendClick = () => {
		if (selectedOption) {
			sendData({
				category: selectedOption.category || '',
				subcategory: selectedOption.subcategory || '',
				type: selectedOption.type || '',
				message,
				fullName: username,
				accountCode: isOthersTab ? billingAccountNumber : numberInfo?.bAccountId || '',
				contactInfo: alternativeNumber,
				assignmentGroup: selectedOption.assignmentGroup || '',
				contactNumber: isOthersTab ? contactNumber : number,
				customerEmail: emailAddress,
				attachments: attachments,
			});
		}
	};

	const onAlternativeNumberChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		if (value.length <= MAX_NUMBER_LENGTH) {
			setAlternativeNumber(value);
		}
	};

	const onEmailAddressChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		event.persist();
		setEmailAddress(value);
	};

	const onContactNumberChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		if (value.length <= MAX_NUMBER_LENGTH) {
			setContactNumber(value);
		}
	};

	const onBillingAccountNumberChange = (event: React.FormEvent<HTMLInputElement>) => {
		const value = (event.target as HTMLInputElement).value;
		setBillingAccountNumber(value);
	};

	const showAlternativeNumberError = useMemo(() => {
		if (isOthersTab) {
			return alternativeNumber === contactNumber && contactNumber.length > 0;
		}
		return numberInfo && alternativeNumber === number;
	}, [isOthersTab, number, alternativeNumber, contactNumber, numberInfo]);

	const showAlternativeEmailError = useMemo(() => {
		return emailAddress ? emailNotValid(emailAddress) : false;
	}, [emailAddress]);

	const showAlternativeContactInput = useMemo(() => {
		if (isOthersTab) {
			return true;
		}
		return !!numberInfo;
	}, [isOthersTab, numberInfo]);

	return (
		<div
			className={classNames(classes.registerTicketSingleTabWrapper, {
				[classes.registerTicketSelectedSingleTabWrapper]: !!numberInfo,
				[classes.registerTicketOthersSingleTabWrapper]: isOthersTab,
			})}
		>
			<ConditionalRender
				show={!numberInfo && !isOthersTab}
				onTrue={() => (
					<BodyS disableMargin color="primary-light">
						{translate('contact.us.register.ticket.enter.number')}
					</BodyS>
				)}
			/>
			<ConditionalRender
				show={!isOthersTab}
				onTrue={() => (
					<div
						className={classNames(classes.registerTicketInputWrapper, {
							[classes.registerTicketInputSelectedWrapper]: !!numberInfo,
						})}
					>
						<Dropdown
							label={numberInfo ? '' : translate('contact.us.register.ticket.input.label')}
							value={number}
							setValue={setNumber}
							suggestions={suggestions}
							loading={loading}
							minSearchTextLength={MIN_SEARCH_TEXT_LENGTH}
							onClick={onSuggestionClick}
							clear={() => setNumberInfo(null)}
							selected={!!numberInfo}
							maxLength={MAX_NUMBER_LENGTH}
							numbersQuantity={numbers.length}
							maxNumberOfSuggestions={MAX_NUMBER_OF_SUGGESTIONS}
						/>
					</div>
				)}
			/>
			<ConditionalRender
				show={!!numberInfo}
				onTrue={() => (
					<NumberInfo
						accountNum={numberInfo?.bAccountId}
						accountType={numberInfo?.accountType}
						number={number}
						documentId={String(singleCorpAccount?.documentId)}
						documentType={singleCorpAccount?.documentType}
					/>
				)}
			/>
			<ConditionalRender
				show={isOthersTab}
				onTrue={() => (
					<>
						<CustomInput
							labelText={translate('contact.us.register.ticket.billing.account.number')}
							id="billingAccountNumber"
							inputProps={{
								required: true,
								value: billingAccountNumber,
								type: 'number',
								onChange: onBillingAccountNumberChange,
							}}
							formControlProps={{
								fullWidth: true,
							}}
						/>
						<CustomInput
							labelText={translate('contact.number')}
							id="contactNumber"
							inputProps={{
								required: true,
								value: contactNumber,
								type: 'number',
								onChange: onContactNumberChange,
							}}
							formControlProps={{
								fullWidth: true,
							}}
						/>
					</>
				)}
			/>
			<ConditionalRender
				show={showAlternativeContactInput}
				onTrue={() => (
					<>
						<CustomInput
							labelText={translate('contact.us.register.ticket.input.alternative.number.label')}
							id="alternativeNumber"
							error={showAlternativeNumberError}
							helperText={
								showAlternativeNumberError &&
								translate('contact.us.register.ticket.input.alternative.number.error')
							}
							inputProps={{
								required: true,
								value: alternativeNumber,
								type: 'number',
								onChange: onAlternativeNumberChange,
							}}
							formControlProps={{
								fullWidth: true,
							}}
						/>
						<CustomInput
							labelText={translate('contact.us.register.ticket.input.email')}
							id="emailAddress"
							helperText={
								showAlternativeEmailError && translate('contact.us.register.ticket.input.email.error')
							}
							inputProps={{
								required: true,
								value: emailAddress,
								type: 'email',
								onChange: onEmailAddressChange,
							}}
							formControlProps={{
								fullWidth: true,
							}}
						/>
					</>
				)}
			/>
			<ConditionalRender
				show={isOthersTab || (isBillingsTab && !!numberInfo)}
				onTrue={() => (
					<div className={classes.selectWrapper}>
						<CustomSelect
							formControlProps={{ fullWidth: true }}
							inputLabelProps={{}}
							selectProps={{ variant: 'standard' }}
							inputLabel={translate('contact.form.subject')}
							items={
								category.select.elements.map((singleElement) => ({
									label: singleElement.displayName,
									value: singleElement.displayName,
								})) || []
							}
							value={selectedSubject}
							onChange={(event: any) => setSelectedSubject(event.target.value)}
							menuClasses={{}}
							className={classes.select}
						/>
					</div>
				)}
			/>
			<ConditionalRender
				show={isOthersTab || !!numberInfo}
				onTrue={() => (
					<>
						<TextField
							InputProps={{ classes: { root: classes.messageWrapper } }}
							multiline
							onChange={(event: any) => {
								event.persist();
								setMessage(event.target.value);
							}}
							value={message}
							fullWidth
							variant="outlined"
							rows={4}
							placeholder={translate('contact.form.message')}
						/>

						<UploadFiles margins={false} formData={attachments} setFormData={setAttachments} />

						<div className={classes.formButtonContainer}>
							<CustomButton color="primary-light" disabled={buttonDisabled} onClick={onSendClick}>
								{translate('send')}
							</CustomButton>
						</div>
					</>
				)}
			/>
		</div>
	);
};

export default SingleTab;
